/* =============================================================
   PLAY! CART DRAWER — behavior
   - Listens to `cart:refresh` event to open + refetch drawer
   - Handles qty change (/cart/change.js) + remove + quick-add
   - Uses Shopify Sections Rendering API for partial updates
   - Dispatches `cart:updated` after every mutation
   - A11y: focus trap, ESC close, restores focus on close
   ============================================================= */
(function () {
  'use strict';

  var SECTION_ID = (window.PlayCart && window.PlayCart.sectionId) || 'cart-drawer';

  var drawer = document.querySelector('[data-cart-drawer]');
  var backdrop = document.querySelector('[data-cart-backdrop]');
  if (!drawer || !backdrop) return;

  var lastFocused = null;

  function isOpen() { return drawer.classList.contains('is-open'); }

  function open() {
    if (isOpen()) return;
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    backdrop.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // move focus to close button
    var closeBtn = drawer.querySelector('[data-cart-close]');
    if (closeBtn) closeBtn.focus();
  }

  function close() {
    if (!isOpen()) return;
    drawer.classList.remove('is-open');
    backdrop.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  function setLoading(state) {
    drawer.classList.toggle('is-loading', !!state);
    drawer.setAttribute('aria-busy', state ? 'true' : 'false');
  }

  // ========== Section render ==========
  function rerenderSection() {
    return fetch('/?sections=' + encodeURIComponent(SECTION_ID), {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data[SECTION_ID];
        if (!html) return;
        var tmp = document.createElement('div');
        tmp.innerHTML = html.trim();
        // Response is a full <section> element; replace the body contents
        var newSection = tmp.querySelector('.shopify-section, [data-cart-section]');
        var fresh = newSection || tmp.firstElementChild;
        if (!fresh) return;

        // Swap inner content of existing nodes rather than the outer element,
        // because event listeners are bound to drawer/backdrop (outer) by reference.
        var newDrawer = fresh.querySelector('[data-cart-drawer]');
        var newBackdrop = fresh.querySelector('[data-cart-backdrop]');
        if (newDrawer) drawer.innerHTML = newDrawer.innerHTML;
        if (newBackdrop) {
          // Preserve is-open state
        }
        document.dispatchEvent(new CustomEvent('cart:updated'));
      });
  }

  // ========== Fetch cart.js (refresh count + open) ==========
  function refreshAndOpen() {
    setLoading(true);
    return rerenderSection()
      .catch(function (err) { console.error('[play-cart] refresh failed', err); })
      .then(function () {
        setLoading(false);
        open();
      });
  }

  // ========== Mutations ==========
  function changeLine(line, quantity) {
    setLoading(true);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return rerenderSection(); })
      .catch(function (err) { console.error('[play-cart] change failed', err); })
      .then(function () { setLoading(false); });
  }

  function addVariant(variantId, quantity) {
    setLoading(true);
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return rerenderSection(); })
      .catch(function (err) { console.error('[play-cart] add failed', err); })
      .then(function () {
        setLoading(false);
        open();
        document.dispatchEvent(new CustomEvent('cart:updated'));
      });
  }

  // ========== Delegated click handlers on the drawer ==========
  drawer.addEventListener('click', function (e) {
    var target = e.target;

    var closeBtn = target.closest('[data-cart-close]');
    if (closeBtn) { e.preventDefault(); close(); return; }

    var qtyBtn = target.closest('[data-qty-change]');
    if (qtyBtn) {
      e.preventDefault();
      var line = parseInt(qtyBtn.getAttribute('data-line'), 10);
      var qty = parseInt(qtyBtn.getAttribute('data-qty'), 10);
      if (!isNaN(line) && !isNaN(qty) && qty >= 0) changeLine(line, qty);
      return;
    }

    var removeBtn = target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      var rline = parseInt(removeBtn.getAttribute('data-line'), 10);
      var item = removeBtn.closest('.cart-item');
      if (item) item.classList.add('is-removing');
      if (!isNaN(rline)) changeLine(rline, 0);
      return;
    }

    var addBtn = target.closest('[data-cart-add]');
    if (addBtn) {
      e.preventDefault();
      var vid = addBtn.getAttribute('data-variant-id');
      if (vid) addVariant(vid, 1);
      return;
    }
  });

  // Qty input direct typing
  drawer.addEventListener('change', function (e) {
    var input = e.target.closest('[data-qty-input]');
    if (!input) return;
    var line = parseInt(input.getAttribute('data-line'), 10);
    var qty = Math.max(0, parseInt(input.value, 10) || 0);
    if (!isNaN(line)) changeLine(line, qty);
  });

  // ========== Global events ==========
  backdrop.addEventListener('click', close);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen()) {
      e.preventDefault();
      close();
    }
  });

  // Listen for cart:refresh from external triggers (header, ATC, quick-add)
  document.addEventListener('cart:refresh', function () {
    refreshAndOpen();
  });

  // Listen for [data-cart-open] globally (header button, etc.)
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-cart-open]');
    if (opener) {
      e.preventDefault();
      refreshAndOpen();
    }
  });

  // Quick-add buttons on product cards (outside the drawer)
  document.addEventListener('click', function (e) {
    var quick = e.target.closest('[data-quick-add]');
    if (!quick) return;
    e.preventDefault();
    var vid = quick.getAttribute('data-variant-id');
    if (!vid) return;
    quick.setAttribute('disabled', 'disabled');
    addVariant(vid, 1).then(function () {
      quick.removeAttribute('disabled');
    });
  });

  // ========== Focus trap ==========
  drawer.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || !isOpen()) return;
    var focusables = drawer.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });
})();

/* =============================================================
   PLAY! WISHLIST — IIFE independiente del cart drawer.
   Funciona en cualquier página (collection, search, home, PDP).
   Storage: localStorage 'play_wishlist_v1' (array de {id, handle, title, added_at}).
   ============================================================= */
(function () {
  'use strict';
  var WL_KEY = 'play_wishlist_v1';

  function readWl() {
    try { return JSON.parse(localStorage.getItem(WL_KEY) || '[]'); } catch (e) { return []; }
  }
  function writeWl(list) {
    try { localStorage.setItem(WL_KEY, JSON.stringify(list)); } catch (e) {}
  }
  function syncAllWishlistButtons() {
    var list = readWl();
    var ids = list.map(function (i) { return String(i.id); });
    document.querySelectorAll('[data-pcard-wishlist]').forEach(function (btn) {
      var pid = String(btn.getAttribute('data-product-id'));
      var saved = ids.indexOf(pid) > -1;
      btn.classList.toggle('is-saved', saved);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      btn.setAttribute('aria-label', saved ? 'Guardado en favoritos' : 'Guardar en favoritos');
    });
    // Sync header badge
    var badge = document.querySelector('[data-wishlist-count]');
    var link = document.querySelector('[data-wishlist-link]');
    if (badge) {
      badge.textContent = list.length;
      badge.classList.toggle('is-empty', list.length === 0);
    }
    if (link) {
      link.classList.toggle('is-active', list.length > 0);
    }
  }

  // Toast micro-feedback al agregar/quitar
  function showToast(message, saved) {
    var existing = document.querySelector('.play-wl-toast');
    if (existing) existing.remove();
    var toast = document.createElement('div');
    toast.className = 'play-wl-toast' + (saved ? ' play-wl-toast--saved' : '');
    toast.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="' + (saved ? 'currentColor' : 'none') + '" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg><span>' + message + '</span>';
    document.body.appendChild(toast);
    requestAnimationFrame(function () { toast.classList.add('is-visible'); });
    setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () { toast.remove(); }, 300);
    }, 2400);
  }

  document.addEventListener('click', function (e) {
    var wl = e.target.closest('[data-pcard-wishlist]');
    if (!wl) return;
    e.preventDefault();
    e.stopPropagation();
    var pid = wl.getAttribute('data-product-id');
    var title = wl.getAttribute('data-product-title') || 'Producto';
    var list = readWl();
    var idx = -1;
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === String(pid)) { idx = i; break; }
    }
    var saved;
    if (idx > -1) {
      list.splice(idx, 1);
      saved = false;
      showToast('Removido de favoritos', false);
    } else {
      list.push({
        id: pid,
        handle: wl.getAttribute('data-product-handle'),
        title: title,
        added_at: Date.now()
      });
      saved = true;
      showToast('Guardado en favoritos', true);
    }
    writeWl(list);
    syncAllWishlistButtons();
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { count: list.length } }));
  });

  // Sync on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAllWishlistButtons);
  } else {
    syncAllWishlistButtons();
  }
})();
